// ============================================================
// useTargetPanelEdits - the Target panel's writes to the active tab
// ============================================================
// Every edit the panel makes to the target goes through `updateTab` on
// the active tab. The decisions are pure (targetPanelModel); this hook
// owns the lock checks and the trait-cleanup flash, which clears itself
// after three seconds.

import { useEffect, useState } from 'react';
import { reResolveParryFromWeapons } from '../engine/parryWeapon';
import { isExposureCode } from '../engine/exposures';
import { withPath } from '../engine/targetEdits';
import { cleanedAfterTraitEdit } from '../engine/traitCleanup';
import { frenzyOn, withCondition, withHealth, withStandingReaction } from './targetPanelModel';

/**
 * @param {object} args `{ target, activeTab, updateTab, locked, stackPolicy }`
 * @returns the flash and the panel's edit verbs. Removing and clearing
 *   conditions and setting health are NOT gated on `locked` here, as they
 *   never were: their controls disable themselves.
 */
export function useTargetPanelEdits(args) {
  const [condFlash, setCondFlash] = useState(null);
  useEffect(() => {
    if (!condFlash) return;
    const timer = setTimeout(() => setCondFlash(null), 3000);
    return () => clearTimeout(timer);
  }, [condFlash]);
  const writes = tabWriters(args, setCondFlash);
  return { condFlash, ...conditionVerbs(writes, args), ...targetVerbs(writes, args) };
}

/** The three ways the panel writes the active tab. */
function tabWriters({ activeTab, updateTab }, setCondFlash) {
  /** A whole new target, relabelling the tab with its name. */
  const commit = (next) => updateTab(activeTab, (t) => ({ ...t, target: next, label: next.name }));
  return {
    commit,
    /** Rewrite the tab's own target (the latest, not this render's). */
    rewrite: (fn) => updateTab(activeTab, (t) => ({ ...t, target: fn(t.target) })),
    /** A cleaned target: the flash first, when it stripped something, then the write. */
    commitCleaned: ({ target: next, flash }) => {
      if (flash) setCondFlash(flash);
      commit(next);
    },
  };
}

function conditionVerbs({ rewrite }, { target, locked, stackPolicy }) {
  const setConditions = (fn) => rewrite((tt) => ({ ...tt, conditions: fn(tt.conditions) }));
  return {
    removeCondition: (ci) => setConditions((cs) => cs.filter((_, j) => j !== ci)),
    clearConditions: () => setConditions((cs) => cs.filter(isExposureCode)),
    clearExposures: () => setConditions((cs) => cs.filter((c) => !isExposureCode(c))),
    addCondition: (code, opts) => {
      if (locked) return;
      // The picker may name a policy for this one (v8.367.0); the session's otherwise.
      const conditions = withCondition(target.conditions, code, opts?.stacking ?? stackPolicy);
      rewrite((tt) => ({ ...tt, conditions }));
    },
  };
}

function targetVerbs({ commit, rewrite, commitCleaned }, { target, activeTab, updateTab, locked }) {
  /** A verb that does nothing while the target is locked. */
  const unlessLocked =
    (fn) =>
    (...a) => {
      if (!locked) fn(...a);
    };
  return {
    setHealth: (value) => rewrite((tt) => withHealth(tt, value)),
    /** The sheet's flat writes (live state, pinned values), with a trait's reactive cleanup. */
    changeTarget: (newTarget) => commitCleaned(cleanedAfterTraitEdit(target, newTarget)),
    toggleFrenzied: unlessLocked(() =>
      target.frenzied ? commit(withPath(target, 'frenzied', false)) : commitCleaned(frenzyOn(target)),
    ),
    reorderWeapons: unlessLocked((newWeapons) => {
      const resolved = reResolveParryFromWeapons({ ...target, defenderWeapons: newWeapons });
      updateTab(activeTab, (t) => ({ ...t, target: { ...t.target, ...resolved }, label: t.target.name }));
    }),
    setStandingReaction: unlessLocked((value) =>
      updateTab(activeTab, (t) => {
        const next = withStandingReaction(t.target, value);
        return { ...t, target: next, label: next.name };
      }),
    ),
  };
}
